import type { HubTierClass, ScoreDetailsV3, ScoreFileDetail } from '../../types';
import { promptContextBand, taskClarityBand } from '../../utils/scoreBands';

export type CoachingTone = 'critical' | 'warning' | 'info' | 'positive';

export interface CoachingTip {
  id: string;
  tone: CoachingTone;
  message: string;
}

export const TIER_LABELS: Record<HubTierClass, string> = {
  org: 'Org-approved',
  domain: 'Domain-approved',
  experimental: 'Experimental',
  unidentified: 'Unidentified',
  rejected: 'Rejected',
};

const FILE_COACHING: Record<HubTierClass, { tone: CoachingTone; message: string }> = {
  rejected: {
    tone: 'critical',
    message:
      'This file was reviewed and rejected by the Hub team — using it costs 1.0 point every time. Swap it for an approved file and the penalty disappears.',
  },
  unidentified: {
    tone: 'warning',
    message:
      "Hub can't find this file in its library — that's a 0.5 point penalty. If you feel this skill is better and deserves a bonus, upload it to the Hub Skills library so an Engineering Manager can review and approve it.",
  },
  experimental: {
    tone: 'info',
    message: "This file is in Hub's review queue — no penalty for using it, but no bonus yet either.",
  },
  org: {
    tone: 'positive',
    message: 'Org-approved — this is the strongest file you can attach. Keep using Hub-approved skills.',
  },
  domain: {
    tone: 'positive',
    message: 'Domain-approved — useful, but an org-approved file of the same kind would earn more.',
  },
};

export function fileCoaching(file: ScoreFileDetail): CoachingTip {
  const copy = FILE_COACHING[file.tier_class] ?? FILE_COACHING.unidentified;
  return {
    id: `file-${file.tier_class}-${file.id ?? file.name ?? 'anon'}`,
    tone: copy.tone,
    message: copy.message,
  };
}

export function reworkCoaching(reworkFlag: boolean): string | null {
  if (!reworkFlag) return null;
  return (
    'This turn was flagged as rework. The −1.0 is not a judgment on how you wrote the prompt — ' +
    'it signals this work should not have been needed in the first place.'
  );
}

export function countCoaching(
  tci: number,
  expected: number,
  actual: number,
  countScore: number
): string | null {
  if (actual === expected) return null;
  const score = countScore.toFixed(2);
  if (actual === 0 && tci >= 7) {
    return (
      `This was a complex task (complexity ${tci}/10). Attaching no skill files only earns ` +
      `a count score of ${score}. Aim for ${expected} approved skill files next time.`
    );
  }
  if (actual > expected) {
    return (
      `You attached ${actual} skill files; ${expected} was the right number for this task. ` +
      `Extra files drop the count score to ${score}.`
    );
  }
  return (
    `You attached ${actual} skill files; this task called for ${expected}. ` +
    `Matching the expected count would raise the count score from ${score}.`
  );
}

export function countFilesByTier(files: ScoreFileDetail[]): Record<HubTierClass, number> {
  const counts: Record<HubTierClass, number> = {
    org: 0,
    domain: 0,
    experimental: 0,
    unidentified: 0,
    rejected: 0,
  };
  for (const file of files) {
    if (file.tier_class in counts) {
      counts[file.tier_class] += 1;
    } else {
      counts.unidentified += 1;
    }
  }
  return counts;
}

export function collectImproveTips(v3: ScoreDetailsV3): CoachingTip[] {
  const tips: CoachingTip[] = [];
  const derived = v3.derived;
  const files = derived.file_details ?? [];

  for (const file of files) {
    if (file.tier_class === 'rejected' || file.tier_class === 'unidentified') {
      const tip = fileCoaching(file);
      tips.push({
        ...tip,
        id: tip.id,
        message: `${file.name || 'This file'}: ${tip.message}`,
      });
    }
  }

  const rework = reworkCoaching(derived.rework_flag);
  if (rework) {
    tips.push({ id: 'rework', tone: 'warning', message: rework });
  }

  const count = countCoaching(
    derived.tci,
    derived.expected_file_count,
    derived.actual_file_count,
    derived.count_score
  );
  if (count) {
    tips.push({ id: 'count', tone: 'info', message: count });
  }

  const tcBand = taskClarityBand(v3.scores.task_clarity);
  if (tcBand && (v3.scores.task_clarity ?? 0) < 9) {
    tips.push({ id: 'tc', tone: 'info', message: `Task clarity: ${tcBand.nextRung}` });
  }
  const pcBand = promptContextBand(v3.scores.prompt_context);
  if (pcBand && (v3.scores.prompt_context ?? 0) < 9) {
    tips.push({ id: 'pc', tone: 'info', message: `Prompt context: ${pcBand.nextRung}` });
  }

  return tips;
}

export function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function derivedBaseScore(v3: ScoreDetailsV3): number {
  const stored = asNumber(v3.derived?.base_score, Number.NaN);
  if (Number.isFinite(stored)) return stored;
  return 0.4 * asNumber(v3.scores.task_clarity) + 0.4 * asNumber(v3.scores.prompt_context);
}

export function justificationNotes(
  justification: ScoreDetailsV3['justification']
): { tc: string; pc: string; oa: string } {
  if (!justification) return { tc: '', pc: '', oa: '' };
  if (typeof justification === 'string') {
    return { tc: justification.trim(), pc: '', oa: '' };
  }
  return {
    tc: justification.tc?.trim() || '',
    pc: justification.pc?.trim() || '',
    oa: justification.oa?.trim() || '',
  };
}

export function formatScore(value: number, digits = 2): string {
  return asNumber(value).toFixed(digits);
}

export function isScoreDetailsV3(details: unknown): details is ScoreDetailsV3 {
  return !!details && typeof details === 'object' && 'v' in details && (details as { v?: number }).v === 3;
}
